import type { SupabaseClient } from '@supabase/supabase-js';

const ORG_STORAGE_KEY = 'current_org_id';
const LEGACY_ORG_STORAGE_KEY = 'active_org_id';

type OrgMemberRow = { org_id: string | null };
type ProfileOrgRow = {
  default_organization_id: string | null;
  invited_via_org_id: string | null;
};
type OrganizationAvailabilityRow = {
  id: string;
};

function persistOrgId(orgId: string | null) {
  if (typeof window === 'undefined') return;
  if (orgId) {
    localStorage.setItem(ORG_STORAGE_KEY, orgId);
    localStorage.setItem(LEGACY_ORG_STORAGE_KEY, orgId);
  } else {
    localStorage.removeItem(ORG_STORAGE_KEY);
    localStorage.removeItem(LEGACY_ORG_STORAGE_KEY);
  }
}

function readStoredOrgId() {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem(ORG_STORAGE_KEY) || localStorage.getItem(LEGACY_ORG_STORAGE_KEY);
}

export async function ensureActiveOrgId(
  supabase: SupabaseClient,
  userId: string,
  orgName?: string
): Promise<string | null> {
  const { data: profile, error: profileError } = await supabase
    .from('profiles')
    .select('default_organization_id, invited_via_org_id')
    .eq('id', userId)
    .maybeSingle<ProfileOrgRow>();

  if (profileError) {
    throw profileError;
  }

  const { data, error } = await supabase
    .from('org_members')
    .select('org_id')
    .eq('user_id', userId);

  if (error) {
    throw error;
  }

  const memberships = (data ?? [])
    .map((row) => (row as OrgMemberRow).org_id)
    .filter((orgId): orgId is string => Boolean(orgId));

  let activeMemberships = memberships;
  if (memberships.length > 0) {
    const { data: orgRows, error: orgError } = await supabase
      .from('organizations')
      .select('id')
      .in('id', memberships)
      .eq('is_archived', false)
      .eq('is_active', true);

    if (orgError) {
      throw orgError;
    }

    const activeOrgIds = new Set(
      ((orgRows ?? []) as OrganizationAvailabilityRow[]).map((row) => row.id),
    );
    activeMemberships = memberships.filter((orgId) => activeOrgIds.has(orgId));
  }

  const preferredOrgIds = [
    readStoredOrgId(),
    profile?.default_organization_id ?? null,
    profile?.invited_via_org_id ?? null,
  ].filter((orgId): orgId is string => Boolean(orgId));

  for (const preferredOrgId of preferredOrgIds) {
    if (activeMemberships.includes(preferredOrgId)) {
      persistOrgId(preferredOrgId);
      return preferredOrgId;
    }
  }

  if (activeMemberships.length > 0) {
    // Never return null when the user still has valid active memberships.
    // Doing so can drop protected project routes out of the authenticated shell
    // even though the session is valid, which presents as "missing" project pages.
    const fallbackOrgId = activeMemberships[0];
    persistOrgId(fallbackOrgId);
    return fallbackOrgId;
  }

  persistOrgId(null);
  return null;
}

export async function getActiveOrgId(
  supabase: SupabaseClient,
  userId: string
): Promise<string | null> {
  return ensureActiveOrgId(supabase, userId);
}
